import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, View } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useLocalSearchParams, useRouter } from "expo-router";
import { collection, query, where, getDocs, doc, setDoc, Timestamp, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { useMembers } from "@/hooks/useMembers";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { MemberListSkeleton } from "@/components/Skeleton";

const Header = styled.View`
  padding: 20px;
`;

const Title = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const Subtitle = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  line-height: 20px;
`;

const SearchInput = styled.TextInput`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  font-size: 15px;
  color: ${colors.ink};
  margin: 0 20px 12px;
`;

const Card = styled.View`
  margin: 0 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;

const Avatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const MemberInfo = styled.View`
  flex: 1;
`;

const MemberName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MemberDetail = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const Checkbox = styled.View<{ checked: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 2px solid ${(p) => p.checked ? colors.brand : colors.line};
  background-color: ${(p) => p.checked ? colors.brand : colors.white};
  align-items: center;
  justify-content: center;
`;

const CheckMark = styled.Text`
  color: #fff;
  font-size: 14px;
  font-weight: ${font.bold};
`;

const SelectedCount = styled.Text`
  font-size: 13px;
  color: ${colors.inkSecondary};
  font-weight: ${font.bold};
  padding: 8px 20px;
`;

const InviteBtn = styled.View<{ disabled?: boolean }>`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 16px 20px;
  opacity: ${(p) => p.disabled ? 0.4 : 1};
`;

const InviteBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
  padding: 32px 20px;
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Spacer = styled.View`
  height: 32px;
`;

export default function ChannelInviteScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { club } = useClub();
  const { members } = useMembers(club?.id);
  const [existingMemberIds, setExistingMemberIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load existing channel members
  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "channels", id, "members"),
      (snap) => {
        const ids = new Set(snap.docs.map((d) => d.id));
        setExistingMemberIds(ids);
        setLoading(false);
      }
    );
    return unsub;
  }, [club?.id, id]);

  // Filter to members not already in channel
  const inviteable = members.filter((m) => !existingMemberIds.has(m.userId));
  const filtered = search
    ? inviteable.filter((m) =>
        m.displayName.toLowerCase().includes(search.toLowerCase()) ||
        m.email.toLowerCase().includes(search.toLowerCase())
      )
    : inviteable;

  const toggleSelect = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((m) => m.userId)));
    }
  };

  const handleInvite = async () => {
    if (!club || !id || selected.size === 0) return;
    setSaving(true);
    for (const userId of selected) {
      const member = members.find((m) => m.userId === userId);
      if (!member) continue;
      await setDoc(doc(db, "clubs", club.id, "channels", id, "members", userId), {
        userId,
        displayName: member.displayName,
        role: "member",
        status: "active",
        joinedAt: Timestamp.now(),
      });
    }
    setSaving(false);
    router.back();
  };

  if (loading) {
    return <View style={{ flex: 1, backgroundColor: colors.bg }}><MemberListSkeleton /></View>;
  }

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Header>
          <Title>Invite Members</Title>
          <Subtitle>Select members to add to this channel.</Subtitle>
        </Header>

        <SearchInput
          placeholder="Search members..."
          placeholderTextColor={colors.inkMuted}
          value={search}
          onChangeText={setSearch}
        />

        {filtered.length > 0 && (
          <Pressable onPress={selectAll}>
            <SelectedCount>
              {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} members available`}
              {" · "}
              {selected.size === filtered.length ? "Deselect all" : "Select all"}
            </SelectedCount>
          </Pressable>
        )}

        {filtered.length > 0 ? (
          <Card>
            {filtered.map((m, i) => (
              <Pressable key={m.id} onPress={() => toggleSelect(m.userId)}>
                <Row bordered={i < filtered.length - 1}>
                  <Avatar>
                    <AvatarText>{m.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
                  </Avatar>
                  <MemberInfo>
                    <MemberName>{m.displayName}</MemberName>
                    <MemberDetail>{m.email}</MemberDetail>
                  </MemberInfo>
                  <Checkbox checked={selected.has(m.userId)}>
                    {selected.has(m.userId) && <CheckMark>✓</CheckMark>}
                  </Checkbox>
                </Row>
              </Pressable>
            ))}
          </Card>
        ) : (
          <EmptyText>All club members are already in this channel.</EmptyText>
        )}

        {selected.size > 0 && (
          <Pressable onPress={handleInvite} disabled={saving}>
            <InviteBtn disabled={saving}>
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <InviteBtnText>Invite {selected.size} member{selected.size !== 1 ? "s" : ""}</InviteBtnText>
              )}
            </InviteBtn>
          </Pressable>
        )}

        <Spacer />
      </ContentWidth>
    </KeyboardAwareScroll>
  );
}
